import { Paper, Select, Skeleton, Text } from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Bar, CartesianGrid, ComposedChart, Customized, Legend, Line, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import type { Vorgangstyp } from "@msc/shared";

import { useKpiTicketMonate, type TicketEingrenzung } from "../../../hooks/useKpiTickets.js";
import { CHART_COLORS } from "../../../theme.js";
import { TicketAuswahlModal, type TicketAuswahl } from "./TicketAuswahlModal.js";
import { TicketBlockKopf } from "./TicketBlockKopf.js";
import { MONAT_KURZ, monatsGrenzen, RECHTE_ACHSE_BREITE, summenZeile } from "./ticketDarstellung.js";

/**
 * SR-02: erstellt und gelöst je Monat als Balken, offener Bestand zum
 * Monatsende als Linie auf einer EIGENEN Achse rechts (wie in eazyBI: Der
 * Bestand ist ein Vielfaches eines Monats, auf einer Achse verschwänden die
 * Balken). Monatsachse wie die Geldkurven darüber; der laufende Monat ist
 * hinterlegt. Ein Klick auf einen Monat zeigt seine erstellten, gelösten und
 * am Monatsende offenen Tickets.
 *
 * Der Vorjahreswert steht als waagerechter Strich AM Balken, nicht als eigene
 * Kurve: Gestrichelte Kurven zwischen den Balken ließen sich keinem Balken
 * zuordnen (Rückmeldung 2026-10-15). Die unsichtbaren Linien darunter halten
 * die Vorjahreswerte im Tooltip und in der Achsenskala.
 */
export function MonatsDiagramm({ segmentId, year, eingrenzung }: { segmentId: string; year: number; eingrenzung?: TicketEingrenzung }) {
  const { t } = useTranslation();
  const [vorgangstyp, setVorgangstyp] = useState<Vorgangstyp | null>(null);
  const [auswahl, setAuswahl] = useState<TicketAuswahl | null>(null);
  const { data, isLoading, isError } = useKpiTicketMonate(segmentId, year, vorgangstyp, eingrenzung);

  const titel = t("pages.kpi.tickets.monate.titel");
  const zeigeMonat = (monat: string) => {
    const g = monatsGrenzen(monat);
    const name = `${MONAT_KURZ[Number(monat.slice(5, 7)) - 1]} ${monat.slice(0, 4)}`;
    const laufend = data?.monate.find((m) => m.monat === monat)?.laufend ?? false;
    setAuswahl({
      titel: t("pages.kpi.tickets.monate.auswahlTitel", { monat: name }),
      abschnitte: [
        { titel: t("pages.kpi.tickets.erstellt"), abfrage: { quelle: "erstellt", ...g, vorgangstyp, ...eingrenzung } },
        { titel: t("pages.kpi.tickets.geloest"), abfrage: { quelle: "geloest", ...g, vorgangstyp, ...eingrenzung } },
        { titel: t(laufend ? "pages.kpi.tickets.offenHeute" : "pages.kpi.tickets.offenMonatsende"), abfrage: { quelle: "offen", ...g, vorgangstyp, ...eingrenzung } },
      ],
    });
  };

  const chartData = (data?.monate ?? []).map((m, i) => ({ name: MONAT_KURZ[i], monat: m.monat, erstellt: m.erstellt, geloest: m.geloest, offen: m.offen ?? undefined, vorjahrErstellt: m.vorjahrErstellt ?? undefined, vorjahrGeloest: m.vorjahrGeloest ?? undefined }));
  const summe = data ? summenZeile(data.monate, data.summe) : null;
  const laufend = chartData.find((d) => data?.monate.find((m) => m.monat === d.monat)?.laufend);

  return (
    <Paper p="md" withBorder>
      <TicketBlockKopf titel={titel} spiegel={data?.spiegel} vorgangstyp={vorgangstyp} onVorgangstyp={setVorgangstyp} />
      {isLoading ? (
        <Skeleton height={280} />
      ) : isError || !data ? (
        <Text size="sm" c="red">{t("pages.kpi.tickets.fehler")}</Text>
      ) : (
        <>
          <Select
            size="xs"
            mb="xs"
            aria-label={t("pages.kpi.tickets.monate.monatAnsehen")}
            placeholder={t("pages.kpi.tickets.monate.monatAnsehen")}
            value={null}
            onChange={(v) => v && zeigeMonat(v)}
            data={data.monate.filter((m) => m.offen !== null).map((m) => ({
              value: m.monat,
              label: `${MONAT_KURZ[Number(m.monat.slice(5, 7)) - 1]} – ${t("pages.kpi.tickets.monate.zaehler", { erstellt: m.erstellt, geloest: m.geloest, offen: m.offen })}`,
            }))}
            style={{ maxWidth: 320 }}
          />
          {summe && <Text size="sm" mb="xs">{t("pages.kpi.tickets.monate.summe", summe)}</Text>}
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart
              data={chartData}
              onClick={(e) => {
                const monat = (e as { activePayload?: Array<{ payload?: { monat?: string; offen?: number } }> } | null)?.activePayload?.[0]?.payload;
                if (monat?.monat && monat.offen !== undefined) zeigeMonat(monat.monat);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="anzahl" allowDecimals={false} />
              <YAxis yAxisId="offen" orientation="right" width={RECHTE_ACHSE_BREITE} allowDecimals={false} label={{ value: t("pages.kpi.tickets.offen"), angle: 90, position: "insideRight" }} />
              <Tooltip />
              <Legend
                payload={[
                  { value: t("pages.kpi.tickets.erstellt"), type: "rect", color: CHART_COLORS.ist },
                  { value: t("pages.kpi.tickets.geloest"), type: "rect", color: CHART_COLORS.positiv },
                  { value: t("pages.kpi.tickets.monate.vorjahrStrich"), type: "plainline", color: VORJAHR_STRICH, payload: { strokeDasharray: "0" } },
                  { value: t("pages.kpi.tickets.offen"), type: "line", color: CHART_COLORS.soll },
                ]}
              />
              {laufend && (
                <ReferenceArea yAxisId="anzahl" x1={laufend.name} x2={laufend.name} fill={CHART_COLORS.neutral} fillOpacity={0.15} label={{ value: t("pages.kpi.tickets.laufend"), position: "insideTop", fontSize: 11 }} />
              )}
              <Bar yAxisId="anzahl" dataKey="erstellt" name={t("pages.kpi.tickets.erstellt")} fill={CHART_COLORS.ist} cursor="pointer" />
              <Bar yAxisId="anzahl" dataKey="geloest" name={t("pages.kpi.tickets.geloest")} fill={CHART_COLORS.positiv} cursor="pointer" />
              <Line yAxisId="anzahl" dataKey="vorjahrErstellt" name={t("pages.kpi.tickets.monate.erstelltVorjahr")} stroke={CHART_COLORS.ist} strokeOpacity={0} dot={false} activeDot={false} legendType="none" />
              <Line yAxisId="anzahl" dataKey="vorjahrGeloest" name={t("pages.kpi.tickets.monate.geloestVorjahr")} stroke={CHART_COLORS.positiv} strokeOpacity={0} dot={false} activeDot={false} legendType="none" />
              <Customized component={VorjahresStriche} />
              <Line yAxisId="offen" dataKey="offen" name={t("pages.kpi.tickets.offen")} stroke={CHART_COLORS.soll} strokeWidth={2} dot type="linear" />
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
      <TicketAuswahlModal segmentId={segmentId} auswahl={auswahl} jiraBaseUrl={data?.jiraBaseUrl ?? null} onClose={() => setAuswahl(null)} />
    </Paper>
  );
}

const VORJAHR_STRICH = "var(--msc-diagramm-vorjahr)";

/** Welcher Balken welchen Vorjahreswert trägt. */
const STRICH_ZU: Record<string, "vorjahrErstellt" | "vorjahrGeloest"> = { erstellt: "vorjahrErstellt", geloest: "vorjahrGeloest" };

interface BalkenPunkt { x: number; width: number; payload?: Record<string, unknown> }
/** Ausschnitt aus dem, was Recharts an `<Customized>` weitergibt (2.15). */
interface ChartZustand {
  formattedGraphicalItems?: Array<{
    item?: { props?: { dataKey?: unknown } };
    props?: { data?: BalkenPunkt[]; yAxis?: { scale?: (v: number) => number } };
  }>;
}

/**
 * Zeichnet je Balken einen Strich in Höhe des Vorjahreswerts. Die Höhe kommt
 * aus der Skala der Achse des Balkens, nicht aus der Balkenhöhe — so steht der
 * Strich auch über einem Monat mit 0 Tickets an der richtigen Stelle.
 */
export function VorjahresStriche(props: unknown) {
  const { formattedGraphicalItems } = props as ChartZustand;
  const striche: ReactElement[] = [];
  for (const g of formattedGraphicalItems ?? []) {
    const schluessel = STRICH_ZU[String(g.item?.props?.dataKey)];
    const skala = g.props?.yAxis?.scale;
    if (!schluessel || !skala) continue;
    for (const punkt of g.props?.data ?? []) {
      const wert = punkt.payload?.[schluessel];
      if (typeof wert !== "number") continue;
      const y = skala(wert);
      striche.push(
        <line key={`${schluessel}-${String(punkt.payload?.monat)}`} x1={punkt.x - 3} x2={punkt.x + punkt.width + 3} y1={y} y2={y} stroke={VORJAHR_STRICH} strokeWidth={3} strokeLinecap="round" pointerEvents="none" />,
      );
    }
  }
  return <g className="vorjahres-striche">{striche}</g>;
}
